import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/support/ticket`, formData);
      setTicketId(res.data.ticket_id);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      console.error('Failed to send message', err);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', message: '' });
    setSubmitted(false);
    setTicketId('');
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
        aria-label="Support Chat"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border border-stone-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white p-4">
            <h3 className="font-semibold text-lg">Need Help?</h3>
            <p className="text-sky-100 text-sm">We're here to assist you</p>
          </div>

          {/* Content */}
          <div className="p-4">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="support-name" className="text-sm">Your Name</Label>
                  <Input
                    id="support-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="support-email" className="text-sm">Email Address</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="support-message" className="text-sm">How can we help?</Label>
                  <Textarea
                    id="support-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Describe your issue or question..."
                    className="mt-1 min-h-[100px] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                >
                  {loading ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-stone-900 mb-2">Message Sent!</h4>
                <p className="text-stone-500 text-sm mb-4">
                  We'll get back to you at <strong>{formData.email}</strong> as soon as possible.
                </p>
                <p className="text-stone-400 text-xs mb-4">
                  Reference: {ticketId.slice(0, 8)}
                </p>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="w-full"
                >
                  Send Another Message
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
